import { jsx as _jsx } from "react/jsx-runtime";
import { Input } from '@/components/ui/input';
import { useState } from 'react';
function clamp(n, min, max) {
    let v = n;
    if (min !== undefined)
        v = Math.max(min, v);
    if (max !== undefined)
        v = Math.min(max, v);
    return v;
}
/**
 * A number field that lets the reader finish typing before it judges.
 *
 * The naive controlled pattern (`onChange={e => set(clamp(Number(e.target.value)))}`)
 * rewrites every keystroke: typing "12" into a 7-14 field clamps the "1" to 7
 * and ends at 14, a lone "-" (value "" while the number is invalid) becomes 0
 * and eats the minus sign, and clearing the field snaps it to 0. iOS/iPadOS
 * number inputs have no spinner, so typing is the only way in there.
 *
 * Here the raw text is a local draft while the field is being edited. Each
 * keystroke that already parses to an in-range number is passed up at once
 * (live previews keep working); anything else waits. Blur or Enter commits
 * the draft clamped into range, or restores the last good value when the
 * draft is not a number at all.
 *
 * No inputMode is set on purpose: callers that never go negative pass
 * inputMode="decimal"; iOS's decimal keypad has no minus key, so a field that
 * can hold a negative level must keep the default number keyboard.
 */
export function DraftNumberInput({ value, onValueChange, min, max, onBlur, onKeyDown, ...props }) {
    // null = not editing: show the committed value.
    const [draft, setDraft] = useState(null);
    const commit = (raw) => {
        const parsed = raw.trim() === '' ? Number.NaN : Number(raw);
        if (Number.isFinite(parsed)) {
            const next = clamp(parsed, min, max);
            if (next !== value)
                onValueChange(next);
        }
        setDraft(null);
    };
    return (_jsx(Input, { ...props, type: "number", min: min, max: max, value: draft ?? String(value), onChange: (e) => {
            const raw = e.target.value;
            setDraft(raw);
            const parsed = raw.trim() === '' ? Number.NaN : Number(raw);
            if (Number.isFinite(parsed) && parsed === clamp(parsed, min, max) && parsed !== value) {
                onValueChange(parsed);
            }
        }, onBlur: (e) => {
            if (draft !== null)
                commit(draft);
            onBlur?.(e);
        }, onKeyDown: (e) => {
            if (e.key === 'Enter' && draft !== null)
                commit(draft);
            onKeyDown?.(e);
        } }));
}
